import { ButtonWithLoading, ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Button, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import type { ProfileItem } from "../../../models/ProfileItem";
import { deleteProfileItems } from "../../../repositories/ProfileItemRepository";

interface BulkDeleteModalProps {
	dimensions: ProfileItem[];
	open: boolean;
	onHide: (deleted: boolean) => void;
}

export const BulkDeleteModal = ({
	dimensions,
	open,
	onHide,
}: BulkDeleteModalProps) => {
	const [error, setError] = useState("");
	const queryClient = useQueryClient();

	const deleteMutation = useMutation(deleteProfileItems, {
		meta: {
			successMessage: "The section dimensions were deleted successfully",
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["sectionDimensions"] }),
				queryClient.invalidateQueries({ queryKey: ["section-spec"] }),
				queryClient.invalidateQueries({ queryKey: ["section-spec-version"] }),
			]);
			onHide(true);
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (deleteMutation.isLoading || dimensions.length < 2) return;

		deleteMutation.mutate(
			dimensions.map((dimension) => dimension.ProfileItemId),
		);
	};

	const handleClose = () => {
		if (!deleteMutation.isLoading) onHide(false);
	};

	return (
		<>
			<MesfModal
				open={open}
				handleClose={handleClose}
				title="DELETE SECTION DIMENSIONS"
				maxWidth="sm"
			>
				<form onSubmit={handleSubmit}>
					<MesfModal.Content dividers>
						<Typography>
							The selected dimensions are used by section specifications.
							Deleting them will retire them from the catalog, create
							replacement subrevisions without them for any affected current or
							scheduled specifications, and remove them from existing drafts. Do
							you want to continue?
						</Typography>
						<Typography component="div" variant="body2" sx={{ mt: 2 }}>
							<strong>Selected dimensions:</strong>
							<ul>
								{[...dimensions]
									.sort((left, right) => left.Code.localeCompare(right.Code))
									.map((dimension) => (
										<li key={dimension.ProfileItemId}>{dimension.Code}</li>
									))}
							</ul>
						</Typography>
					</MesfModal.Content>
					<MesfModal.Actions>
						<Button
							onClick={handleClose}
							color="inherit"
							disabled={deleteMutation.isLoading}
						>
							Cancel
						</Button>
						<ButtonWithLoading
							isLoading={deleteMutation.isLoading}
							disabled={deleteMutation.isLoading || dimensions.length < 2}
							color="secondary"
							variant="contained"
							type="submit"
						>
							Delete
						</ButtonWithLoading>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Deleting Section Dimensions"
			/>
		</>
	);
};
